import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { authAPI } from "../utils/api";
import { FiLock, FiUser, FiAlertCircle } from "react-icons/fi";

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [kakaoLoading, setKakaoLoading] = useState(false);
  const [error, setError] = useState("");

  // 카카오 SDK 초기화
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://developers.kakao.com/sdk/js/kakao.js";
    script.async = true;
    script.onload = () => {
      window.Kakao.init("0f4bcfff7febf94f0867ea62aa819899"); // 실제 카카오 앱 키
    };
    document.body.appendChild(script);

    return () => {
      // 컴포넌트 언마운트 시 추가된 스크립트 제거
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // 일반 로그인 처리
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setError("아이디와 비밀번호를 모두 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await authAPI.signIn({
        username,
        password,
        is_mobile: false,
      });

      localStorage.setItem("accessToken", response.data.accessToken);
      localStorage.setItem("refreshToken", response.data.refreshToken);
      localStorage.setItem("username", username);
      localStorage.setItem("isKakao", "false");

      navigate("/", { replace: true });
    } catch (err) {
      console.error("로그인 에러:", err);
      if (err.response?.status === 400) {
        setError("아이디 또는 비밀번호를 확인해주세요.");
      } else if (err.response?.status === 422) {
        setError(err.response.data || "로그인 처리 중 오류가 발생했습니다.");
      } else {
        setError("로그인 중 오류가 발생했습니다. 다시 시도해 주세요.");
      }
    } finally {
      setLoading(false);
    }
  };

  // 카카오 로그인 처리
  const handleKakaoLogin = () => {
    setKakaoLoading(true);
    setError("");

    if (!window.Kakao) {
      setError(
        "카카오 로그인을 위한 SDK를 로드하는 중입니다. 잠시 후 다시 시도해주세요."
      );
      setKakaoLoading(false);
      return;
    }

    const loginTimeoutId = setTimeout(() => {
      setKakaoLoading(false);
      setError("로그인 요청 시간이 초과되었습니다. 다시 시도해주세요.");
    }, 30000);

    window.Kakao.Auth.login({
      success: async (authObj) => {
        clearTimeout(loginTimeoutId);

        try {
          const response = await authAPI.authKakao({
            kakaoAccessToken: authObj.access_token,
            is_mobile: false,
          });

          localStorage.setItem("accessToken", response.data.accessToken);
          localStorage.setItem("refreshToken", response.data.refreshToken);
          localStorage.setItem("isKakao", "true");

          window.Kakao.API.request({
            url: "/v2/user/me",
            success: function (res) {
              const nickname =
                res.kakao_account?.profile?.nickname || res.id.toString();
              localStorage.setItem("username", nickname);
              navigate("/", { replace: true });
            },
            fail: function (error) {
              console.error("카카오 사용자 정보 요청 실패", error);
              navigate("/", { replace: true });
            },
          });
        } catch (err) {
          console.error("카카오 로그인 에러:", err);
          setError("카카오 로그인 처리 중 오류가 발생했습니다.");
          setKakaoLoading(false);
        }
      },
      fail: (err) => {
        clearTimeout(loginTimeoutId);
        console.error("카카오 로그인 실패:", err);
        setError("카카오 로그인에 실패했습니다.");
        setKakaoLoading(false);
      },
    });
  };

  return (
    <Container className="py-5" style={{ minHeight: "85vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        .login-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 149, 144, 0.05);
          border: 1px solid rgba(0, 149, 144, 0.08);
          width: 100%;
          max-width: 460px;
          transition: all 0.3s ease;
        }

        .login-logo-container {
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(0, 149, 144, 0.05);
          width: 80px;
          height: 80px;
          border-radius: 20px;
          margin: 0 auto 1.5rem auto;
          transition: all 0.3s ease;
        }

        .login-logo-container img {
          width: 48px;
          height: 48px;
        }

        .gradient-title {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          letter-spacing: -0.5px;
        }

        .form-input-custom {
          border-radius: 10px;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border: 1px solid #edf2f7;
          background-color: #f7fafc;
          transition: all 0.2s ease;
          font-size: 0.95rem;
        }
        
        .form-input-custom:focus {
          border-color: var(--main-color);
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(0, 149, 144, 0.15);
          outline: none;
        }
        
        .input-icon-wrapper {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          font-size: 1.1rem;
          transition: color 0.2s ease;
          z-index: 10;
        }
        
        .form-input-custom:focus + .input-icon {
          color: var(--main-color);
        }

        .btn-gradient {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          padding: 0.75rem;
          transition: all 0.25s ease;
          box-shadow: 0 4px 15px rgba(0, 149, 144, 0.15);
        }
        
        .btn-gradient:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 149, 144, 0.25);
          background: linear-gradient(135deg, var(--main-hover) 0%, #00605c 100%);
        }

        .btn-kakao {
          background-color: #FEE500;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          color: #191919;
          padding: 0.75rem;
          transition: all 0.25s ease;
          box-shadow: 0 4px 15px rgba(254, 229, 0, 0.15);
        }

        .btn-kakao:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(254, 229, 0, 0.25);
          background-color: #fada0a;
        }
      `}</style>

      <Card className="login-card shadow-lg border-0">
        <Card.Body className="p-4 p-sm-5">
          <div className="login-logo-container">
            <img
              src="/images/logo1.svg"
              alt="비나이다 로고"
              className="img-fluid"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>

          <h2 className="text-center gradient-title mb-4">비나이다</h2>

          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <div className="input-icon-wrapper">
                <Form.Control
                  type="text"
                  placeholder="아이디 입력"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading || kakaoLoading}
                  className="form-input-custom w-100"
                  required
                />
                <FiUser className="input-icon" />
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="input-icon-wrapper">
                <Form.Control
                  type="password"
                  placeholder="비밀번호 입력"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || kakaoLoading}
                  className="form-input-custom w-100"
                  required
                />
                <FiLock className="input-icon" />
              </div>
            </Form.Group>

            {error && (
              <Alert variant="danger" className="mb-3 d-flex align-items-center" style={{ borderRadius: "10px" }}>
                <FiAlertCircle className="me-2" style={{ fontSize: "1.2rem", flexShrink: 0 }} />
                <div style={{ fontSize: "0.9rem" }}>{error}</div>
              </Alert>
            )}

            <Button
              variant="primary"
              type="submit"
              className="btn-gradient w-100 mb-3"
              disabled={loading || kakaoLoading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">로그인 중...</span>
                </>
              ) : (
                "아이디로 로그인"
              )}
            </Button>

            <Button
              className="btn-kakao w-100 mb-3 d-flex align-items-center justify-content-center"
              onClick={handleKakaoLogin}
              disabled={loading || kakaoLoading}
            >
              {kakaoLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">처리 중...</span>
                </>
              ) : (
                <>
                  <img
                    src="/images/kakao_symbol.svg"
                    alt="카카오 로고"
                    style={{ height: "18px", marginRight: "8px" }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  카카오 로그인
                </>
              )}
            </Button>

            <div className="text-center mt-4">
              <span className="text-muted small">
                계정이 없으신가요?{" "}
                <Link
                  to="/register"
                  style={{
                    color: "var(--main-color)",
                    fontWeight: "600",
                    textDecoration: "underline",
                  }}
                >
                  회원가입 하기
                </Link>
              </span>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login;
